export default {
    name: 'statisticsservice',
    dependencies: [],
    callable() {
        const firstPublishedKey = 'firstPublished';
        const defaultCountersKey = 'defaultCounters';
        const repeatVisitorsKey = 'repeativeVisitsCounters';
        const timeSeriesKey = 'timeSeriesCounters';
        const countersKey = 'counters';
        const counterValuesKey = 'dailyValues';
        const descriptionKey = 'description';
        const adViewsTotalsKey = 'adViewsTotal';
        const counterColorKey = 'rgbaColorValue';
        const distVisitorsKey = 'distributionVisitors';

        let _displayRange;

        function createUrl(element) {
            const url = new URL('/ad/adstatistics.json', document.baseURI);
            ['finnkode', 'firstpublished', 'enddatetime', 's']
                .filter(name => element.hasAttribute(`data-${name}`))
                .forEach(name =>
                    url.searchParams.set(
                        name,
                        element.getAttribute(`data-${name}`)
                    )
                );
            return url;
        }

        function getDisplayRange(daysSincePublished, range) {
            return Math.min(daysSincePublished, range);
        }

        function findMaxXAxisValue(daysSincePublished) {
            const minValue = 7;
            return Math.max(daysSincePublished - 1, minValue - 1); // -1 to get zero-indexed value
        }

        function findMinXAxisValue(daysSincePublished, range) {
            return daysSincePublished - range;
        }

        /** Removes days that are outside of the range, and summarizes the rest */
        function summarizePeriod(values, daysSincePublished, range) {
            return Object.keys(values)
                .filter(dayIdx => {
                    const daysNotInRange = daysSincePublished - range;
                    return dayIdx > daysNotInRange;
                })
                .reduce((previous, current) => previous + values[current], 0);
        }

        function createChartColumn(elements, graphName, counter) {
            const counters = new Array(elements).fill(null);
            counters[0] = graphName;

            const dailyCounters = counter[counterValuesKey];
            for (const day in dailyCounters) {
                if (dailyCounters.hasOwnProperty(day)) {
                    counters[day] = dailyCounters[day];
                }
            }
            return counters;
        }

        function getGraphColors(statistics) {
            const colors = {};
            const counterDesc = statistics[adViewsTotalsKey][descriptionKey];
            colors[counterDesc] = statistics[adViewsTotalsKey][counterColorKey];
            const timeSeriesCounters = statistics[timeSeriesKey][countersKey];
            timeSeriesCounters.forEach(timeSeriesCounter => {
                colors[timeSeriesCounter[descriptionKey]] =
                    timeSeriesCounter[counterColorKey];
            });
            return colors;
        }

        function createGraphGroup(timeSeriesCounters) {
            const graphGroup = [];
            timeSeriesCounters.forEach(timeSeriesCounter => {
                graphGroup.push(timeSeriesCounter[descriptionKey]);
            });
            return graphGroup;
        }

        function getCountersByDay(timeSeriesCounters, elements) {
            const dailyStatisticsSortedByCounters = [];
            timeSeriesCounters.forEach(timeSeriesCounter => {
                dailyStatisticsSortedByCounters.push(
                    createChartColumn(
                        elements,
                        timeSeriesCounter[descriptionKey],
                        timeSeriesCounter
                    )
                );
            });
            return dailyStatisticsSortedByCounters;
        }

        function getDaysSincePublished(totalViewsCounter) {
            return Object.keys(totalViewsCounter[counterValuesKey]).length;
        }

        function getCounterTotals(statistics, daysSincePublished, range) {
            let periodText = '';
            if (range === daysSincePublished) {
                periodText = 'i hele perioden';
            } else if (range === 14) {
                periodText = 'siste 14 dagene';
            } else if (range === 30) {
                periodText = 'siste 30 dagene';
            }

            const counters = statistics[timeSeriesKey][countersKey];
            const counterModels = counters.map(counter => ({
                description: counter.description,
                color: counter.rgbaColorValue,
                totalValue: summarizePeriod(
                    counter.dailyValues,
                    daysSincePublished,
                    range
                ),
                unit: counter.unit,
            }));

            const sumPeriod = counterModels.reduce(
                (previous, current) => previous + current.totalValue,
                0
            );

            return {
                periodDescription: periodText,
                counters: counterModels,
                sumPeriodValue: sumPeriod,
                sumPeriodUnit: 'visninger',
            };
        }

        function getVisitorTotals(statistics) {
            const model = {};
            if (statistics[repeatVisitorsKey] !== null) {
                model.repeativeVisits = statistics[repeatVisitorsKey];
                model.defaultStatistics = statistics[defaultCountersKey];
                model.distributionVisitors = statistics[distVisitorsKey];
            }
            return model;
        }

        function createVisitorsChart(element, visitors) {
            const data = [];
            let aggregatedPst = 0;
            visitors.distributionVisitors.forEach(category => {
                if (category.present) {
                    const pst =
                        category.distributionVisitorCount /
                        category.totalVisitorCount;
                    data.push({
                        name: category.label,
                        val: pst,
                        color: category.color,
                    });
                    aggregatedPst += pst;
                }
            });

            data.push({
                name: 'Vanlig besøkende',
                val: 1 - aggregatedPst,
                color: '#B6F0FF',
            });

            const width = 200;
            const height = 200;
            const donut = d3.layout.pie();
            const radius = Math.min(width, height) / 2;
            const arc = d3.svg
                .arc()
                .innerRadius(radius * 0.6)
                .outerRadius(radius);

            const chart = d3
                .select(element)
                .append('svg:svg')
                .data([data])
                .attr('width', width + 75)
                .attr('height', height + 55);

            const arcs = chart
                .selectAll('g.arc')
                .data(donut.value(d => d.val))
                .enter()
                .append('svg:g')
                .attr('class', 'arc')
                .attr('transform', `translate(${radius + 40},${radius + 30})`);

            arcs
                .append('svg:path')
                .attr('fill', d => d.data.color)
                .attr('d', arc);

            arcs
                .append('svg:text')
                .attr('transform', d => {
                    const centroid = arc.centroid(d);
                    const x = centroid[0];
                    const y = centroid[1];
                    const hyp = Math.sqrt(x * x + y * y);
                    return `translate(${x / hyp * (radius + 15)},${y /
                        hyp *
                        (radius + 15)})`;
                })
                .attr('class', 't5')
                .attr(
                    'text-anchor',
                    d =>
                        (d.endAngle + d.startAngle) / 2 > Math.PI
                            ? 'end'
                            : 'start'
                )
                .text(d => `${Math.round(100 * d.value).toFixed()}%`);

            return chart;
        }

        function createCountersChart(element, data, daysSincePublished, range) {
            const months = [
                'jan',
                'feb',
                'mar',
                'apr',
                'mai',
                'jun',
                'jul',
                'aug',
                'sep',
                'okt',
                'nov',
                'des',
            ];

            const elements = daysSincePublished + 1; // +1 because the name of the graph is in index 0.
            const timeSeriesCounters = data[timeSeriesKey][countersKey];
            const dailyValues = getCountersByDay(timeSeriesCounters, elements);
            const graphGroup = createGraphGroup(timeSeriesCounters);
            const graphColors = getGraphColors(data);

            // HACK TO AVOID AN EMPTY CHART WITH NO AXIS.
            const dummyCounterName = 'dummyCounterToAvoidEmptyChart';
            dailyValues.push([dummyCounterName, 1]);
            graphGroup.push(dummyCounterName);
            graphColors[dummyCounterName] = 'rgba(255, 255, 255, 0)';

            _displayRange = range;
            const chart = c3.generate({
                bindto: element,
                data: {
                    type: 'bar',
                    colors: graphColors,
                    groups: [graphGroup],
                    columns: dailyValues,
                    order: null, // Set to null to keep the order of the counters in the columns-data
                },
                legend: {
                    show: false,
                },
                tooltip: {
                    show: false,
                },
                point: {
                    show: false,
                },
                interaction: {
                    enabled: false,
                },
                grid: {
                    x: {
                        show: false,
                    },
                    y: {
                        show: true,
                    },
                },
                axis: {
                    x: {
                        height: 47,
                        tick: {
                            culling: false,
                            rotate: 60,
                            format(dayIdx) {
                                const firstPublished = new Date(
                                    data[firstPublishedKey]
                                );
                                const date = new Date(firstPublished);
                                date.setDate(
                                    firstPublished.getDate() + parseInt(dayIdx)
                                );

                                const ticks = 7;
                                const visibleTicks = Math.ceil(
                                    _displayRange / ticks
                                );
                                const daysBeforeDisplayedRange =
                                    daysSincePublished - _displayRange;
                                const isDayInRange =
                                    dayIdx >= daysBeforeDisplayedRange;
                                const isTickVisible =
                                    dayIdx % Math.floor(visibleTicks) === 0;
                                if (isDayInRange && isTickVisible) {
                                    return `${date.getDate()}. ${months[
                                        date.getMonth()
                                    ]}`;
                                }
                                return '';
                            },
                        },
                        min: findMinXAxisValue(daysSincePublished, range),
                        max: findMaxXAxisValue(daysSincePublished),
                    },
                    y: {
                        padding: { top: 7, bottom: 0 },
                        tick: {
                            format(yValue) {
                                return yValue === Math.floor(yValue)
                                    ? yValue
                                    : '';
                            },
                        },
                        max: findMaxYAxisValue(daysSincePublished, range, data),
                        min: 0,
                    },
                },
                bar: {
                    width: {
                        max: 35,
                    },
                },
            });

            // HACK TO HIDE X-AXIS TICK LABEL OUTSIDE CHART CANVAS.
            const elemFix = document.createElement('div');
            elemFix.id = 'xAxisTickLabelFix';
            elemFix.style.height = '40px';
            elemFix.style.width = '40px';
            elemFix.style.position = 'absolute';
            elemFix.style.left = '0px';
            elemFix.style.bottom = '0px';
            elemFix.style.background = 'white';
            elemFix.style.zIndex = '1';
            element.appendChild(elemFix);

            return chart;
        }

        function setChartRange(chart, daysSincePublished, range, data) {
            _displayRange = range;
            if (chart) {
                chart.axis.range({
                    max: {
                        x: findMaxXAxisValue(daysSincePublished),
                        y: findMaxYAxisValue(daysSincePublished, range, data),
                    },
                    min: {
                        x: findMinXAxisValue(daysSincePublished, range),
                    },
                });
            }
        }

        function findMaxYAxisValue(daysSincePublished, range, data) {
            const elements = daysSincePublished + 1; // +1 because the name of the graph is in index 0.
            const timeSeriesCounters = data[timeSeriesKey][countersKey];
            const dailyValues = getCountersByDay(timeSeriesCounters, elements);

            return (
                dailyValues
                    .map(arr => arr.slice(1))
                    .reduce((acc, current) => {
                        const tmp = [];
                        for (let i = 0; i < acc.length; i++) {
                            tmp[i] = acc[i] + current[i];
                        }
                        return tmp;
                    })
                    .slice(-range)
                    .reduce((acc, current) => Math.max(acc, current)) + 50
            );
        }

        return {
            findMaxXAxisValue,
            findMinXAxisValue,
            createUrl,
            getDisplayRange,
            summarizePeriod,
            getDaysSincePublished,
            getCounterTotals,
            getVisitorTotals,
            createCountersChart,
            createVisitorsChart,
            setChartRange,
        };
    },
};



// WEBPACK FOOTER //
// ./services/statisticsservice.js